import React, { useEffect } from 'react';
import useStandaloneAuth from '@/hooks/useStandaloneAuth';
import Loading from '@/components/atoms/Loader';
import ConfirmActionModal from '@/components/organism/ConfirmActionModal';
import { handleErrorClose } from '@/standalone';
import { STANDALONE_CONSTANTS } from '../constants';

interface AuthValidatorProps {
  sid?: string | null;
  sig?: string | null;
  redirectOnSuccess: string;
}

/**
 * AuthValidator component - validates session params and redirects to products page on success
 */
const AuthValidator: React.FC<AuthValidatorProps> = ({
  sid,
  sig,
  redirectOnSuccess,
}) => {
  const { isLoading, isValidated, error } = useStandaloneAuth({ sid, sig });

  // Redirect to products page ONLY on successful validation (no error)
  useEffect(() => {
    if (!isLoading && isValidated && !error) {
      // Redirect to base path with hash (e.g., /#/smartcooler-approved-products)
      window.location.href = `${window.location.origin}/#${redirectOnSuccess}`;
    }
  }, [isLoading, isValidated, error, redirectOnSuccess]);

  // Show loading state while validating
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading />
      </div>
    );
  }

  // Show error modal if validation failed
  if (error) {
    return (
      <>
        <ConfirmActionModal
          title={STANDALONE_CONSTANTS.SESSION_EXPIRED_MODAL.TITLE}
          description={error}
          confirmLabel={STANDALONE_CONSTANTS.SESSION_EXPIRED_MODAL.INFO_BUTTON}
          isOpen
          onClose={handleErrorClose}
          onConfirm={handleErrorClose}
          variant="warning"
        />
        <div className="filter blur-sm pointer-events-none min-h-screen" />
      </>
    );
  }

  // Show loading while redirect happens
  return (
    <div className="flex items-center justify-center min-h-screen">
      <Loading />
    </div>
  );
};

export default AuthValidator;
